import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { AccessTokenData, Condition } from '@empathyco/playboard-core-domain'
import { getCurrentInstance } from 'vue'
import { AUTH_CONSTS } from '../../store/auth/auth.consts'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { ArrayUtils } from '../../utils/array.utils'
import { mapGetters } from '../utils'
import { useRoles } from './use-roles'

export function useConditionValues(): {
  getConditionValues: (condition: Condition) => Array<string> | undefined
} {
  const ctx = getCurrentInstance()
  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }

  const { tokenInfo } = mapGetters<AccessTokenData>(ctx.proxy.$store, AUTH_CONSTS.moduleName, {
    tokenInfo: AUTH_CONSTS.getters.tokenInfo,
  })

  const { instances } = mapGetters<Array<Instance>>(ctx.proxy.$store, CONFIG_CONSTS.moduleName, {
    instances: CONFIG_CONSTS.getters.instances,
  })

  const {
    data: { list: rolesData },
  } = useRoles()

  function getTokenConditionValues(condition: Condition): Array<string> {
    return (
      tokenInfo.value.authorization.resources
        .find(authorizationResource => authorizationResource.name === 'UserRoleManagement')
        ?.conditions.reduce<Array<string>>((result, authorizationResourceCondition) => {
          authorizationResourceCondition[condition]?.forEach(
            authorizationResourceConditionValue => {
              if (!result.includes(authorizationResourceConditionValue)) {
                result.push(authorizationResourceConditionValue)
              }
            },
          )
          return result
        }, []) ?? []
    )
  }

  function getConditionValues(condition: Condition): Array<string> | undefined {
    let result: Array<string> | undefined
    switch (condition) {
      case 'instance':
      case 'role-id':
        result = getTokenConditionValues(condition)
        if (result.includes('*')) {
          if (condition === 'instance') {
            result = ArrayUtils.addUniqueItems(
              result,
              instances.value.map(instance => instance.id),
            )
          } else {
            result = ArrayUtils.addUniqueItems(
              result,
              rolesData.value?.results.map(role => `${role.id}`) ?? [],
            )
          }
        }
        break
      case 'access-level':
        result = ['READ', 'WRITE']
        break
      default:
        result = undefined
        break
    }
    return result
  }

  return {
    getConditionValues,
  }
}
